import React, { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
    addCompanyCategory,
    CompanyCategory,
    CompanyCategoryInput,
    getCompanyCategories,
    setCompanyCategoryStatus,
    updateCompanyCategory
} from '../../api/companyCategories';
import { CommonDispatcher, GetCategories } from '../../redux/Common/action';
import { SHOW_SNACKBAR } from '../../redux/Common/actionTypes';
import { connect } from 'react-redux';
import { useCookies } from 'react-cookie';

const DISABLE_WARNING =
    'Existing company profiles using this category will remain saved, but this category will no longer appear for new selections.';

const useStyles = makeStyles((theme) => ({
    header: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2)
    },
    form: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2)
    },
    tableContainer: {
        maxHeight: 'calc(100vh - 310px)'
    },
    inactiveRow: {
        backgroundColor: '#f7f7f7',
        '& td': {
            color: '#777'
        }
    },
    actionsCell: {
        whiteSpace: 'nowrap'
    },
    actionsWrapper: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(1)
    },
    helperText: {
        color: theme.palette.text.secondary
    }
}));

interface FormState {
    label: string;
    url_identifier: string;
    rank: string;
}

interface CompanyCategoriesProps {
    CommonDispatcher: (type: string, payload: object) => void;
    GetCategories: (token: string) => Promise<unknown>;
}

const emptyForm: FormState = {
    label: '',
    url_identifier: '',
    rank: ''
};

const toSlug = (value: string): string =>
    value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

const toInput = (form: FormState): CompanyCategoryInput => ({
    label: form.label.trim(),
    url_identifier: toSlug(form.url_identifier || form.label),
    rank: form.rank === '' ? null : Number(form.rank)
});

const toForm = (category: CompanyCategory): FormState => ({
    label: category.label || '',
    url_identifier: category.url_identifier || '',
    rank: category.rank === null || category.rank === undefined ? '' : String(category.rank)
});

const CompanyCategories = ({
    CommonDispatcher,
    GetCategories
}: CompanyCategoriesProps) => {
    const classes = useStyles();
    const [cookies] = useCookies(['pgAdminToken']);
    const token = cookies['pgAdminToken'];
    const [categories, setCategories] = useState<CompanyCategory[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [form, setForm] = useState<FormState>(emptyForm);
    const [editing, setEditing] = useState<CompanyCategory | null>(null);
    const [editForm, setEditForm] = useState<FormState>(emptyForm);
    const [statusCategory, setStatusCategory] =
        useState<CompanyCategory | null>(null);
    const [isStatusSaving, setIsStatusSaving] = useState(false);

    const sortedCategories = useMemo(
        () =>
            [...categories].sort((a, b) => {
                const aRank = a.rank === null ? Number.MAX_SAFE_INTEGER : a.rank;
                const bRank = b.rank === null ? Number.MAX_SAFE_INTEGER : b.rank;
                return aRank - bRank || a.category_id - b.category_id;
            }),
        [categories]
    );

    const showMessage = (message: string, type = 'success') => {
        CommonDispatcher(SHOW_SNACKBAR, {
            isShowing: true,
            message,
            type
        });
    };

    const refreshCommonCategories = () => {
        if (token) {
            GetCategories(token);
        }
    };

    const loadCategories = async () => {
        setIsLoading(true);
        const result = await getCompanyCategories();
        if (result.type === 'success') {
            setCategories(result.response || []);
        } else {
            showMessage(
                result.response.message || 'Failed to load company categories.',
                'error'
            );
        }
        setIsLoading(false);
    };

    useEffect(() => {
        loadCategories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const validate = (data: CompanyCategoryInput): string | null => {
        if (!data.label) {
            return 'Label is required.';
        }
        if (!data.url_identifier) {
            return 'Url identifier is required.';
        }
        if (data.rank !== null && Number.isNaN(data.rank)) {
            return 'Rank must be numeric.';
        }
        return null;
    };

    const handleAdd = async (event: React.FormEvent) => {
        event.preventDefault();
        const data = toInput(form);
        const error = validate(data);
        if (error) {
            showMessage(error, 'error');
            return;
        }

        setIsSaving(true);
        const result = await addCompanyCategory(data);
        if (result.type === 'success') {
            setCategories((current) => [...current, result.response]);
            setForm(emptyForm);
            refreshCommonCategories();
            showMessage('Company category added.');
        } else {
            showMessage(
                result.response.message || 'Failed to add company category.',
                'error'
            );
        }
        setIsSaving(false);
    };

    const handleEdit = (category: CompanyCategory) => {
        setEditing(category);
        setEditForm(toForm(category));
    };

    const handleUpdate = async () => {
        if (!editing) return;
        const data = toInput(editForm);
        const error = validate(data);
        if (error) {
            showMessage(error, 'error');
            return;
        }

        setIsSaving(true);
        const result = await updateCompanyCategory(editing.category_id, data);
        if (result.type === 'success') {
            setCategories((current) =>
                current.map((category) =>
                    category.category_id === result.response.category_id
                        ? result.response
                        : category
                )
            );
            setEditing(null);
            refreshCommonCategories();
            showMessage('Company category updated.');
        } else {
            showMessage(
                result.response.message || 'Failed to update company category.',
                'error'
            );
        }
        setIsSaving(false);
    };

    const saveStatus = async (
        category: CompanyCategory,
        isEnabled: boolean
    ) => {
        setIsStatusSaving(true);
        const result = await setCompanyCategoryStatus(
            category.category_id,
            isEnabled
        );
        if (result.type === 'success') {
            setCategories((current) =>
                current.map((item) =>
                    item.category_id === result.response.category_id
                        ? result.response
                        : item
                )
            );
            refreshCommonCategories();
            showMessage(
                `Company category ${isEnabled ? 'enabled' : 'disabled'}.`
            );
        } else {
            showMessage(
                result.response.message ||
                    'Failed to update company category status.',
                'error'
            );
        }
        setIsStatusSaving(false);
        setStatusCategory(null);
    };

    const renderForm = (
        currentForm: FormState,
        setCurrentForm: React.Dispatch<React.SetStateAction<FormState>>,
        submitLabel?: string
    ) => (
        <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} sm={4}>
                <TextField
                    label="Label *"
                    value={currentForm.label}
                    onChange={(event) =>
                        setCurrentForm((current) => ({
                            ...current,
                            label: event.target.value
                        }))
                    }
                    variant="outlined"
                    size="small"
                    fullWidth
                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <TextField
                    label="Url identifier"
                    value={currentForm.url_identifier}
                    onChange={(event) =>
                        setCurrentForm((current) => ({
                            ...current,
                            url_identifier: event.target.value
                        }))
                    }
                    placeholder={toSlug(currentForm.label)}
                    variant="outlined"
                    size="small"
                    fullWidth
                />
            </Grid>
            <Grid item xs={12} sm={submitLabel ? 2 : 4}>
                <TextField
                    label="Rank"
                    value={currentForm.rank}
                    onChange={(event) =>
                        setCurrentForm((current) => ({
                            ...current,
                            rank: event.target.value
                        }))
                    }
                    type="number"
                    variant="outlined"
                    size="small"
                    fullWidth
                />
            </Grid>
            {submitLabel ? (
                <Grid item xs={12} sm={2}>
                    <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                        disabled={isSaving}
                        fullWidth
                    >
                        {isSaving ? (
                            <CircularProgress size="1.2rem" color="inherit" />
                        ) : (
                            submitLabel
                        )}
                    </Button>
                </Grid>
            ) : null}
        </Grid>
    );

    return (
        <Container maxWidth="lg">
            <div className={classes.header}>
                <Typography variant="h5">Company Categories</Typography>
                <Typography variant="body2" className={classes.helperText}>
                    Manage tenant-specific Biz Services categories.
                </Typography>
            </div>

            <Paper className={classes.form}>
                <form onSubmit={handleAdd}>
                    {renderForm(form, setForm, 'Add')}
                </form>
            </Paper>

            <TableContainer component={Paper} className={classes.tableContainer}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Label</TableCell>
                            <TableCell>Url identifier</TableCell>
                            <TableCell>Rank</TableCell>
                            <TableCell>Count</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    <CircularProgress size="1.5rem" />
                                </TableCell>
                            </TableRow>
                        ) : null}
                        {!isLoading && sortedCategories.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    No company categories found.
                                </TableCell>
                            </TableRow>
                        ) : null}
                        {!isLoading
                            ? sortedCategories.map((category) => (
                                  <TableRow
                                      key={category.category_id}
                                      className={
                                          category.is_enabled
                                              ? undefined
                                              : classes.inactiveRow
                                      }
                                  >
                                      <TableCell>{category.label}</TableCell>
                                      <TableCell>
                                          {category.url_identifier}
                                      </TableCell>
                                      <TableCell>
                                          {category.rank ?? '-'}
                                      </TableCell>
                                      <TableCell>{category.count}</TableCell>
                                      <TableCell>
                                          <Chip
                                              size="small"
                                              label={
                                                  category.is_enabled
                                                      ? 'Enabled'
                                                      : 'Disabled'
                                              }
                                              color={
                                                  category.is_enabled
                                                      ? 'primary'
                                                      : 'default'
                                              }
                                          />
                                      </TableCell>
                                      <TableCell
                                          align="right"
                                          className={classes.actionsCell}
                                      >
                                          <div
                                              className={
                                                  classes.actionsWrapper
                                              }
                                          >
                                              <Button
                                                  type="button"
                                                  size="small"
                                                  color="primary"
                                                  variant="outlined"
                                                  onClick={() =>
                                                      handleEdit(category)
                                                  }
                                              >
                                                  Edit
                                              </Button>
                                              <Button
                                                  type="button"
                                                  size="small"
                                                  color={
                                                      category.is_enabled
                                                          ? 'secondary'
                                                          : 'primary'
                                                  }
                                                  variant="contained"
                                                  disabled={isStatusSaving}
                                                  onClick={() => {
                                                      if (
                                                          category.is_enabled
                                                      ) {
                                                          setStatusCategory(
                                                              category
                                                          );
                                                      } else {
                                                          saveStatus(
                                                              category,
                                                              true
                                                          );
                                                      }
                                                  }}
                                              >
                                                  {category.is_enabled
                                                      ? 'Disable'
                                                      : 'Enable'}
                                              </Button>
                                          </div>
                                      </TableCell>
                                  </TableRow>
                              ))
                            : null}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog
                open={Boolean(editing)}
                onClose={() => setEditing(null)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Edit Company Category</DialogTitle>
                <DialogContent>
                    {renderForm(editForm, setEditForm)}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditing(null)} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleUpdate}
                        color="primary"
                        variant="contained"
                        disabled={isSaving}
                    >
                        {isSaving ? (
                            <CircularProgress size="1.2rem" color="inherit" />
                        ) : (
                            'Save'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(statusCategory)}
                onClose={() => setStatusCategory(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Disable Company Category</DialogTitle>
                <DialogContent>
                    <DialogContentText>{DISABLE_WARNING}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setStatusCategory(null)}
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() =>
                            statusCategory &&
                            saveStatus(statusCategory, false)
                        }
                        color="secondary"
                        variant="contained"
                        disabled={isStatusSaving}
                    >
                        {isStatusSaving ? (
                            <CircularProgress size="1.2rem" color="inherit" />
                        ) : (
                            'Disable'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default connect(null, { CommonDispatcher, GetCategories })(
    CompanyCategories
);
